import { PricingPlansType } from "@/components/PricingPlans";
import * as Schema from "../types/schema";
import { Product } from "../types/schema";
import { State, getStateSelectorImage, statesQuery } from "../types/state";

import { excludeDraftsFilter } from "./filters";
import { TestimonialCarouselProps } from "@/components/SlidingTestimonialsCarousel";
import { HeaderNavigationProps } from "@/components/HeaderNavigation";
import { Colorways } from "src/theme";
import { SEOPageData } from "@/types/seoPageData";
import { MultiColumnSelectionType } from "@/components/MultiColumnSection";
import { FooterNavigationProps } from "@/components/FooterNavigation/query";

export type PricingLandingData = Pick<
  Schema.PricingLanding,
  "_id" | "pageFeatureFlag" | "hero"
> & {
  colorway: Colorways;
  hero: Schema.PricingLandingHero;
  moduleProductsInfo?: {
    moduleFeatureFlag: Schema.FeatureFlag;
    productsInfo: Pick<
      Schema.ProductsDescription,
      | "textAdditionalInformationLink"
      | "textCTA"
      | "scheduleTime"
      | "featureComparison"
      | "urlFeatureComparison"
      | "unavailableState"
      | "unselectedState"
    > & {
      productsInfo: Array<{
        _key: string;
        product: Product;
        description: string;
        features: string[];
        urlAdditionalInformationLink: string;
        urlCTA: string;
      }>;
    };
  };
  states: State[];
  image: Schema.CustomImage;
  moduleAdoptionTaxCredit: {
    moduleFeatureFlag: Schema.FeatureFlag;
    moduleInput: Schema.AdoptionTaxCredit;
  };
  moduleProductsComparison: {
    moduleFeatureFlag: Schema.FeatureFlag;
    moduleInput: Pick<
      Schema.ProductsComparison,
      | "heading"
      | "subHeading"
      | "stateSelector"
      | "unselectedState"
      | "unavailableState"
      | "footer"
    > & {
      products: Array<{
        product: Schema.Product;
        ctaURL: string;
      }>;
      features: Schema.ProductFeatures[];
      featureGroups: Schema.PricingFeatureGroup[];
    };
  };
  moduleQuestions: {
    moduleFeatureFlag: Schema.FeatureFlag;
    moduleInput: Pick<
      Schema.Questions,
      | "heading"
      | "subHeading"
      | "cta1Text"
      | "cta1URL"
      | "cta2Text"
      | "cta2URL"
      | "image"
    > & {
      testimonials: Schema.Testimonial[];
    };
  };
  pricing: PricingPlansType;
  testimonials: TestimonialCarouselProps;
  navSections: HeaderNavigationProps["navSections"];
  providers: [MultiColumnSelectionType];
  footerPayload: FooterNavigationProps;
} & SEOPageData;

export const getPricingLandingQuery = (preview?: boolean) => `
  *[_type == "pricingLanding"${!preview ? ` && ${excludeDraftsFilter}` : ""}]{
    _id,
    seo,
    pageFeatureFlag,
    colorway,
    hero,
    moduleProductsInfo{
      moduleFeatureFlag,
      productsInfo{
        textAdditionalInformationLink,
        textCTA,
        scheduleTime,
        featureComparison,
        urlFeatureComparison,
        unavailableState,
        unselectedState,
        productsInfo[]{
          _key,
          product->,
          description,
          features,
          urlAdditionalInformationLink,
          urlCTA
        },

      }
    },
    "states": ${statesQuery},
    "image":${getStateSelectorImage(preview)}.image,
    moduleAdoptionTaxCredit,
    moduleProductsComparison{
      moduleFeatureFlag,
      moduleInput{
        heading,
        subHeading,
        stateSelector,
        unselectedState,
        unavailableState,
        footer,
        products[]{
          product->,
          ctaURL
        },
        features[]->,
        featureGroups[]->
      }
    },
    moduleQuestions{
      moduleFeatureFlag,
      moduleInput{
        heading,
        subHeading,
        cta1Text,
        cta1URL,
        cta2Text,
        cta2URL,
        image,
        testimonials[]->
      }
    },
    pricing[0]->{
      sectionIntro,
      pricingPlans[] {...}
    },
    testimonials[0]-> {...},
    providers[]-> {...}
  }
`;
